/** MEN-94 canonical evaluation identity and public, credential-free model evidence. */
export type BenchmarkModelErrorCode =
  | 'PROVIDER_NOT_CONFIGURED'
  | 'PROVIDER_CREDENTIAL_CHANGED'
  | 'MODEL_CONFIGURATION_REQUIRED'
  | 'MODEL_LOOKUP_FAILED'
  | 'MODEL_ACCESS_DENIED'
  | 'MODEL_CATALOG_LIMIT'
  | 'MODEL_CATALOG_UNAVAILABLE'
  | 'MODEL_NOT_ACCESSIBLE'
  | 'MODEL_RETIRED';
export type BenchmarkModelProvenanceStatus = 'MATCHED' | 'UNKNOWN' | 'MISMATCH' | 'MIXED';
export type BenchmarkModelLifecycle = 'ACTIVE' | 'DEPRECATED' | 'RETIRED' | 'UNKNOWN';
export interface BenchmarkEvaluationSelection {
  executionModel: string;
  scorerModel: string;
  deprecatedModelAcknowledged?: boolean;
}
export interface BenchmarkEvaluationConfig {
  schemaVersion: 1;
  evaluationMethodVersion: string;
  execution: { provider: 'ANTHROPIC'; model: string; maxTokens: number };
  scorer: { provider: 'ANTHROPIC'; model: string; maxTokens: number; inputContractVersion: string };
}
export interface BenchmarkModelPrice {
  currency: 'USD';
  inputPerMillionTokens: number;
  outputPerMillionTokens: number;
  source: string;
  verifiedAt: string;
  validUntil: string;
  policyVersion: string;
}
export interface BenchmarkModelSuitability {
  status: 'KNOWN_SUITABLE' | 'UNKNOWN';
  source: string | null;
  verifiedAt: string | null;
  validUntil: string | null;
  policyVersion: string | null;
}
export interface BenchmarkModelMetadata {
  lifecycle: BenchmarkModelLifecycle;
  lifecycleSource: string | null;
  lifecycleVerifiedAt: string | null;
  lifecycleValidUntil: string | null;
  lifecyclePolicyVersion: string | null;
  price: BenchmarkModelPrice | null;
  suitability: BenchmarkModelSuitability;
}
export interface BenchmarkModelCatalogItem extends BenchmarkModelMetadata {
  id: string;
  displayName: string;
  identity: 'PINNED' | 'ALIAS' | 'UNKNOWN';
}
export interface BenchmarkModelCatalogResponse {
  provider: 'ANTHROPIC';
  source: 'LIVE' | 'CACHE' | 'UNAVAILABLE';
  fetchedAt: string | null;
  cacheAgeSeconds: number | null;
  models: BenchmarkModelCatalogItem[];
  errorCode: BenchmarkModelErrorCode | null;
}
export interface BenchmarkModelPhaseResolutionEvidence extends BenchmarkModelMetadata {
  requestedModel: string;
  resolvedModel: string;
  identity: 'PINNED';
}
export interface BenchmarkModelResolutionEvidence {
  resolutionVersion: 'anthropic-model-resolution/v1';
  resolvedAt: string;
  execution: BenchmarkModelPhaseResolutionEvidence;
  scorer: BenchmarkModelPhaseResolutionEvidence;
}
export const BENCHMARK_MODEL_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,254}$/;
export const BENCHMARK_METHOD_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/;
export const BENCHMARK_CONFIG_DIGEST_PATTERN = /^[a-f0-9]{64}$/;
const errorCodes: readonly BenchmarkModelErrorCode[] = [
  'PROVIDER_NOT_CONFIGURED',
  'PROVIDER_CREDENTIAL_CHANGED',
  'MODEL_CONFIGURATION_REQUIRED',
  'MODEL_LOOKUP_FAILED',
  'MODEL_ACCESS_DENIED',
  'MODEL_CATALOG_LIMIT',
  'MODEL_CATALOG_UNAVAILABLE',
  'MODEL_NOT_ACCESSIBLE',
  'MODEL_RETIRED',
];
export function isBenchmarkModelErrorCode(value: unknown): value is BenchmarkModelErrorCode {
  return typeof value === 'string' && errorCodes.includes(value as BenchmarkModelErrorCode);
}
// Never read inherited credential-bearing fields or invoke getters during public projection.
function own(value: unknown, key: string): unknown {
  if (value === null || typeof value !== 'object') return undefined;
  return Object.getOwnPropertyDescriptor(value, key)?.value;
}
function containsControls(value: string): boolean {
  return Array.from(value).some(
    (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127
  );
}
function identifier(value: unknown, pattern = BENCHMARK_MODEL_ID_PATTERN): string {
  if (typeof value !== 'string' || containsControls(value) || !pattern.test(value.trim()))
    throw new Error('Invalid evaluation identifier.');
  return value.trim();
}
function phase(input: unknown) {
  const provider = own(input, 'provider');
  const maxTokens = own(input, 'maxTokens');
  if (
    provider !== 'ANTHROPIC' ||
    typeof maxTokens !== 'number' ||
    !Number.isInteger(maxTokens) ||
    maxTokens < 1 ||
    maxTokens > 2147483647
  )
    throw new Error('Invalid evaluation phase settings.');
  return { provider, model: identifier(own(input, 'model')), maxTokens } as const;
}
/** Structural validation is independent of runtime admission/capability authorization. */
export function createBenchmarkEvaluationConfig(input: unknown): BenchmarkEvaluationConfig {
  if (own(input, 'schemaVersion') !== 1)
    throw new Error('Unsupported evaluation configuration schema.');
  return {
    schemaVersion: 1,
    evaluationMethodVersion: identifier(
      own(input, 'evaluationMethodVersion'),
      BENCHMARK_METHOD_ID_PATTERN
    ),
    execution: phase(own(input, 'execution')),
    scorer: {
      ...phase(own(input, 'scorer')),
      inputContractVersion: identifier(
        own(own(input, 'scorer'), 'inputContractVersion'),
        BENCHMARK_METHOD_ID_PATTERN
      ),
    },
  };
}
export function projectBenchmarkEvaluationConfig(input: unknown): BenchmarkEvaluationConfig | null {
  try {
    return createBenchmarkEvaluationConfig(input);
  } catch {
    return null;
  }
}
/** Web Crypto works in Node 22 and secure browser contexts; no server-only bundle dependency. */
export async function benchmarkEvaluationConfigDigest(
  input: BenchmarkEvaluationConfig
): Promise<string> {
  const config = createBenchmarkEvaluationConfig(input);
  // The exact owned insertion order is the versioned canonical serialization.
  const bytes = new TextEncoder().encode(JSON.stringify(config));
  const crypto = (
    globalThis as unknown as {
      crypto: { subtle: { digest(algorithm: string, bytes: Uint8Array): Promise<ArrayBuffer> } };
    }
  ).crypto;
  const result = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(result), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
function text(input: unknown): string | null {
  return typeof input === 'string' &&
    input.length > 0 &&
    input.length <= 2048 &&
    !containsControls(input)
    ? input
    : null;
}
function date(input: unknown): string | null {
  return typeof input === 'string' &&
    /^\d{4}-\d{2}-\d{2}T/.test(input) &&
    Number.isFinite(Date.parse(input))
    ? input
    : null;
}
function positiveEvidence(input: unknown) {
  const source = text(own(input, 'source')),
    verifiedAt = date(own(input, 'verifiedAt')),
    validUntil = date(own(input, 'validUntil')),
    policyVersion = text(own(input, 'policyVersion'));
  return source &&
    verifiedAt &&
    validUntil &&
    policyVersion &&
    Date.parse(validUntil) > Date.parse(verifiedAt) &&
    Date.parse(validUntil) - Date.parse(verifiedAt) <= 30 * 86400000
    ? { source, verifiedAt, validUntil, policyVersion }
    : null;
}
function metadata(input: unknown): BenchmarkModelMetadata {
  const rawLifecycle = own(input, 'lifecycle');
  const evidence = positiveEvidence({
    source: own(input, 'lifecycleSource'),
    verifiedAt: own(input, 'lifecycleVerifiedAt'),
    validUntil: own(input, 'lifecycleValidUntil'),
    policyVersion: own(input, 'lifecyclePolicyVersion'),
  });
  const lifecycle: BenchmarkModelLifecycle =
    ['ACTIVE', 'DEPRECATED', 'RETIRED'].includes(String(rawLifecycle)) && evidence
      ? (rawLifecycle as BenchmarkModelLifecycle)
      : 'UNKNOWN';
  const rawPrice = own(input, 'price'),
    priceEvidence = positiveEvidence(rawPrice);
  const inputRate = own(rawPrice, 'inputPerMillionTokens'),
    outputRate = own(rawPrice, 'outputPerMillionTokens');
  const price: BenchmarkModelPrice | null =
    own(rawPrice, 'currency') === 'USD' &&
    priceEvidence &&
    typeof inputRate === 'number' &&
    Number.isFinite(inputRate) &&
    inputRate >= 0 &&
    typeof outputRate === 'number' &&
    Number.isFinite(outputRate) &&
    outputRate >= 0
      ? {
          currency: 'USD',
          inputPerMillionTokens: inputRate,
          outputPerMillionTokens: outputRate,
          ...priceEvidence,
        }
      : null;
  const rawSuitable = own(input, 'suitability'),
    suitableEvidence = positiveEvidence(rawSuitable);
  const suitability: BenchmarkModelSuitability =
    own(rawSuitable, 'status') === 'KNOWN_SUITABLE' && suitableEvidence
      ? { status: 'KNOWN_SUITABLE', ...suitableEvidence }
      : {
          status: 'UNKNOWN',
          source: null,
          verifiedAt: null,
          validUntil: null,
          policyVersion: null,
        };
  return {
    lifecycle,
    lifecycleSource: lifecycle === 'UNKNOWN' ? null : evidence!.source,
    lifecycleVerifiedAt: lifecycle === 'UNKNOWN' ? null : evidence!.verifiedAt,
    lifecycleValidUntil: lifecycle === 'UNKNOWN' ? null : evidence!.validUntil,
    lifecyclePolicyVersion: lifecycle === 'UNKNOWN' ? null : evidence!.policyVersion,
    price,
    suitability,
  };
}
export function projectBenchmarkModelCatalogItem(input: unknown): BenchmarkModelCatalogItem | null {
  try {
    const id = identifier(own(input, 'id')),
      displayName = text(own(input, 'displayName')) ?? id;
    const identity = own(input, 'identity');
    return {
      id,
      displayName,
      identity: identity === 'PINNED' || identity === 'ALIAS' ? identity : 'UNKNOWN',
      ...metadata(input),
    };
  } catch {
    return null;
  }
}
export function projectBenchmarkModelCatalogResponse(
  input: unknown
): BenchmarkModelCatalogResponse {
  const rawSource = own(input, 'source'),
    rawModels = own(input, 'models'),
    fetchedAt = date(own(input, 'fetchedAt'));
  const source =
    own(input, 'provider') === 'ANTHROPIC' &&
    (rawSource === 'LIVE' || rawSource === 'CACHE') &&
    fetchedAt &&
    Array.isArray(rawModels)
      ? rawSource
      : 'UNAVAILABLE';
  const age = own(input, 'cacheAgeSeconds'),
    code = own(input, 'errorCode');
  return {
    provider: 'ANTHROPIC',
    source,
    fetchedAt,
    cacheAgeSeconds: typeof age === 'number' && Number.isFinite(age) && age >= 0 ? age : null,
    models:
      source === 'UNAVAILABLE'
        ? []
        : (rawModels as unknown[])
            .map(projectBenchmarkModelCatalogItem)
            .filter((item): item is BenchmarkModelCatalogItem => item !== null),
    errorCode: isBenchmarkModelErrorCode(code)
      ? code
      : source === 'UNAVAILABLE'
        ? 'MODEL_CATALOG_UNAVAILABLE'
        : null,
  };
}
export function projectBenchmarkModelResolutionEvidence(
  input: unknown
): BenchmarkModelResolutionEvidence | null {
  try {
    const resolvedAt = date(own(input, 'resolvedAt'));
    if (own(input, 'resolutionVersion') !== 'anthropic-model-resolution/v1' || !resolvedAt)
      return null;
    const project = (phaseInput: unknown): BenchmarkModelPhaseResolutionEvidence => {
      if (own(phaseInput, 'identity') !== 'PINNED') throw new Error('Unverified model resolution.');
      return {
        requestedModel: identifier(own(phaseInput, 'requestedModel')),
        resolvedModel: identifier(own(phaseInput, 'resolvedModel')),
        identity: 'PINNED',
        ...metadata(phaseInput),
      };
    };
    return {
      resolutionVersion: 'anthropic-model-resolution/v1',
      resolvedAt,
      execution: project(own(input, 'execution')),
      scorer: project(own(input, 'scorer')),
    };
  } catch {
    return null;
  }
}
